import { authForgetPassword } from '@/redux/actions';
import * as Constants from '@/redux/constants';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { Alert, Divider, Stack, Typography } from '@mui/material';
import { useDialogs } from '@toolpad/core';
import icon from 'assets/login-page-icon.png';
import { Button } from 'components/button';
import { TextInput } from 'components/input';
import { LanguageSwitcher } from 'components/language-switcher';
import { useDispatch, useLanguageNavigate, useLocale, useSelector } from 'hooks';
import { useI18n } from 'locales';
import get from 'lodash/get';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { shallowEqual } from 'react-redux';

const selector = (state) => ({
  authForgetPasswordInfo: state.authForgetPasswordInfo,
});

function ForgotPasswordPage() {
  const dispatch = useDispatch();
  const navigate = useLanguageNavigate();
  const locale = useLocale();
  const dialogs = useDialogs();
  const t = useI18n();
  const { authForgetPasswordInfo } = useSelector(selector, shallowEqual);
  const { formState: { errors }, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      email: '',
    },
  });

  const isFetching = authForgetPasswordInfo.isFetching;
  const watchedEmail = watch('email');

  useEffect(() => {
    switch (authForgetPasswordInfo.type) {
      case Constants.AUTH_FORGET_PASSWORD.SUCCESS:
        // Redirect to verify page
        navigate(`/forgot-password/verify?email=${watchedEmail}`);
        break;
      case Constants.AUTH_FORGET_PASSWORD.FAILURE:
        // Error is displayed inline
        break;
    }
  }, [authForgetPasswordInfo, navigate, watchedEmail]);

  const onSubmit = useCallback((data) => {
    dispatch(authForgetPassword({ payload: data }));
  }, [dispatch]);

  const onError = useCallback(() => {
    dialogs.alert(t('Please enter a valid email address.'), {
      title: t('Invalid Input'),
      okText: t('OK')
    });
  }, [dialogs, t]);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit, onError)}
      gap={4}
      sx={{
        backgroundColor: 'background.default',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        position: 'relative',
      }}
    >
      <Stack direction="row" justifyContent="flex-end" sx={{ width: '100%', mt: -2, px: 2 }}>
        <LanguageSwitcher />
      </Stack>

      <Stack gap={2} sx={{ alignItems: 'center', display: 'flex', width: isMobile ? '90%' : '280px' }}>
        <img src={icon} alt='icon' style={{ width: '100px', height: '100px' }} />

        <Typography variant='h5' color='secondary.main' sx={{ textAlign: 'center', fontWeight: 'bold' }}>
          {t('GIVING')}
        </Typography>
      </Stack>

      <Stack sx={{ width: isMobile ? '95%' : '500px', flexDirection: 'column', display: 'flex', alignItems: 'center' }}>
        <Typography variant='h2' color='secondary.main' sx={{ p: 2, fontWeight: 'bold' }}>
          {t('FORGOT PASSWORD')}
        </Typography>
        <Divider sx={{ width: '95%', height: '4px', backgroundColor: 'primary.main' }} />
      </Stack>

      <Stack gap={2} sx={{ alignItems: 'center', display: 'flex', width: isMobile ? '90%' : '500px' }}>
        <Typography variant='body1' sx={{ textAlign: 'center', px: 2 }}>
          {t('Enter your email address and we\'ll send you a verification code to reset your password.')}
        </Typography>

        {authForgetPasswordInfo.error && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {get(authForgetPasswordInfo, 'error.error.description', t('Failed to send verification code. Please try again.'))}
          </Alert>
        )}

        <TextInput
          label={t('Email')}
          type="email"
          value={watch('email')}
          onChange={(e) => setValue('email', e.target.value)}
          error={!!errors.email}
          helper={errors.email?.message}
          fullWidth
          placeholder={t('your.email@example.com')}
        />
      </Stack>

      <Stack sx={{ display: 'flex', width: '300px' }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          leading={faEnvelope}
          fullWidth
          disabled={isFetching || !watch('email')}
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          {isFetching ? t('Sending...') : t('Send Verification Code')}
        </Button>
      </Stack>

      <Typography variant='body2'>
        <a href={`/giving/login?locale=${locale}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
          {t('Back to Login')}
        </a>
      </Typography>
    </Stack >
  );
}

export default ForgotPasswordPage;
